import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import CloneIconButton from 'components/CloneIconButton';
import DeleteIconButton from 'components/DeleteIconButton';
import EditIconButton from 'components/EditIconButton';
import Progress from 'components/Progress';
import { SortableField, SortDirection, SortingPreferences, DEFAULT_SORT_DIRECTION } from 'types/Sorting';
import Template from 'types/Template';
import { cloneTemplatePath, templatePath } from 'routes';
import { useCollection } from 'utils/useCollection';

const getSortDirection = (field: SortableField, sortingPreferences: SortingPreferences): SortDirection => {
  if (field === sortingPreferences.field) {
    return sortingPreferences.direction;
  }

  return DEFAULT_SORT_DIRECTION;
};

interface TemplatesTableProps {
  loadingTemplates: boolean;
  sortingPreferences: SortingPreferences;
  templates: Template[];
  onChangeSorting: (payload: { field: SortableField; direction: SortDirection }) => void;
  onDelete: (template: Template) => void;
}

const TemplatesTable: React.FC<TemplatesTableProps> = ({
  loadingTemplates,
  sortingPreferences,
  templates,
  onChangeSorting,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { collection } = useCollection();

  const createSortHandler = (field: SortableField) => {
    return () => {
      const newSortDirection =
        field === sortingPreferences.field ? (sortingPreferences.direction === 'asc' ? 'desc' : 'asc') : 'asc';

      onChangeSorting({ field, direction: newSortDirection });
    };
  };

  if (!collection) return null;

  if (loadingTemplates) {
    return <Progress />;
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <TableSortLabel
              active={sortingPreferences.field === 'template_name'}
              direction={getSortDirection('template_name', sortingPreferences)}
              onClick={createSortHandler('template_name')}
            >
              Template
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              active={sortingPreferences.field === 'reference_label'}
              direction={getSortDirection('reference_label', sortingPreferences)}
              onClick={createSortHandler('reference_label')}
            >
              Ref. Label
            </TableSortLabel>
          </TableCell>
          <TableCell>Template Type</TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {templates.map((t) => (
          <TableRow key={t.id}>
            <TableCell>{t.name}</TableCell>
            <TableCell>{t.reference_label}</TableCell>
            <TableCell>{t.type}</TableCell>
            <TableCell>
              <EditIconButton onClick={() => navigate(templatePath(collection.id, t.id))} />
            </TableCell>
            <TableCell>
              <CloneIconButton onClick={() => navigate(cloneTemplatePath(collection.id, t.id))} />
            </TableCell>
            <TableCell>
              <DeleteIconButton onClick={() => onDelete(t)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default TemplatesTable;
